/* ───────────────────── TEAMS + ROSTERS CSV MAPPER ─────────────────────
   Turns a CSV (one row per player) into grouped teams ready to create.
   See public/templates/README.md for the format spec. */
import { parseCsv } from "./csv";

const HEADER_ALIASES = {
  team: ["team name", "team", "teamname"],
  ageGroup: ["age group", "age", "agegroup", "age-group"],
  parentPasscode: ["parent passcode", "parent", "parentpasscode", "passcode"],
  coachPasscode: ["coach passcode", "coach", "coachpasscode"],
  player: ["player", "player name", "name", "playername"],
  jersey: ["jersey", "jersey number", "number", "jersey #", "#"],
  parentEmail: ["parent email", "email", "parentemail", "guardian email", "contact email"],
};

const POSITIONAL = { team: 0, ageGroup: 1, parentPasscode: 2, coachPasscode: 3, player: 4, jersey: 5, parentEmail: 6 };

function slugify(str) {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

function isHeaderRow(row) {
  const lowered = row.map((c) => c.trim().toLowerCase());
  const hasTeam = lowered.some((c) => HEADER_ALIASES.team.includes(c));
  const hasPlayer = lowered.some((c) => HEADER_ALIASES.player.includes(c));
  return hasTeam && hasPlayer;
}

function buildColumnMap(headerRow) {
  const map = {};
  headerRow.forEach((cell, idx) => {
    const c = cell.trim().toLowerCase();
    for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
      if (map[key] === undefined && aliases.includes(c)) map[key] = idx;
    }
  });
  return map;
}

/* Returns { teams: [...], errors: [...], totalPlayers, hadHeader }.
   Each team: { id, name, ageGroup, parentPasscode, coachPasscode, parentAuto, coachAuto, players:[{name,jersey}] } */
export function parseTeamsRosters(text) {
  const result = { teams: [], errors: [], totalPlayers: 0, hadHeader: false };
  const rows = parseCsv(text);
  if (rows.length === 0) { result.errors.push("The file is empty."); return result; }

  let colMap, dataRows;
  if (isHeaderRow(rows[0])) {
    colMap = buildColumnMap(rows[0]);
    dataRows = rows.slice(1);
    result.hadHeader = true;
  } else {
    colMap = POSITIONAL;
    dataRows = rows;
  }

  if (colMap.team === undefined || colMap.player === undefined) {
    result.errors.push("Couldn't find a Team Name column and a Player column. Check the header row.");
    return result;
  }

  const byTeam = new Map();
  dataRows.forEach((row, i) => {
    const get = (key) => (colMap[key] !== undefined ? (row[colMap[key]] || "").trim() : "");
    const teamName = get("team");
    const playerName = get("player");
    const lineNo = i + 1 + (result.hadHeader ? 1 : 0);

    if (!teamName) {
      result.errors.push(`Line ${lineNo}: no team name — row skipped.`);
      return;
    }
    const id = slugify(teamName);
    if (!id) {
      result.errors.push(`Line ${lineNo}: team name "${teamName}" has no usable characters — skipped.`);
      return;
    }

    if (!byTeam.has(id)) {
      const rawParent = get("parentPasscode");
      const rawCoach = get("coachPasscode");
      const parentPasscode = rawParent || id;
      byTeam.set(id, {
        id,
        name: teamName,
        ageGroup: get("ageGroup"),
        parentPasscode,
        coachPasscode: rawCoach || parentPasscode + "-coach",
        parentAuto: !rawParent,
        coachAuto: !rawCoach,
        players: [],
        _seen: new Set(),
      });
    }

    const t = byTeam.get(id);
    if (!t.ageGroup && get("ageGroup")) t.ageGroup = get("ageGroup");

    if (playerName) {
      const pslug = slugify(playerName);
      if (pslug && !t._seen.has(pslug)) {
        t._seen.add(pslug);
        t.players.push({
          name: playerName,
          jersey: get("jersey"),
          parentEmail: get("parentEmail").toLowerCase(),
        });
      }
    }
  });

  result.teams = Array.from(byTeam.values()).map(({ _seen, ...t }) => t);
  result.totalPlayers = result.teams.reduce((n, t) => n + t.players.length, 0);

  const empty = result.teams.filter((t) => t.players.length === 0).map((t) => t.name);
  if (empty.length) result.errors.push(`No players found for: ${empty.join(", ")}.`);

  return result;
}
